/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { NEW_PATH_DELIMITER } from '@picsio/db/src/constants';
import { makeTree, setField } from '../helpers/savedSearches';
import { addSavedSearch } from './add';

export const remove = createAsyncThunk(
  'savedSearches/remove',
  async (
    {
      _id, name, showToast = true, showDialog = true,
    },
    {
      extra: {
        sdk, Logger, Toast, localization: l18n, showErrorDialog, showDialogAsync, utils,
      },
      getState, dispatch,
    },
  ) => {
    if (showDialog) {
      let dialogTitle = l18n.TAGSTREE.titleRemoveSavedSearch;
      let dialogText = l18n.TAGSTREE.textYouAreAboutRemoveSavedSearch(name);
      let hasSiblings;
      const { all } = getState().savedSearches;
      const search = all.find((ss) => ss._id === _id);
      const { path } = search || {};
      if (path) {
        // we need to check is there any saved searches in the same group, if yes - we will show different message in the dialog
        const pathWithoutName = path.split(NEW_PATH_DELIMITER).slice(0, -1).join(NEW_PATH_DELIMITER);
        hasSiblings = all.some((ss) => ss._id !== _id && (ss.path || '').startsWith(pathWithoutName + NEW_PATH_DELIMITER));
        if (!hasSiblings) {
          dialogTitle = l18n.TAGSTREE.titleRemoveSavedSearchAndGroup;
          dialogText = l18n.TAGSTREE.textYouAreAboutRemoveSavedSearchWithGroup(pathWithoutName.split(NEW_PATH_DELIMITER).pop());
        }
      }
      try {
        Logger.log('UI', 'ConfirmDeleteSavedSearch');
        await showDialogAsync({
          title: dialogTitle,
          text: dialogText,
          textBtnOk: l18n.DIALOGS.btnDelete,
          textBtnCancel: l18n.DIALOGS.btnCancel,
          onCancel: () => {
            if (path && !hasSiblings) {
              Logger.log('User', 'SavedSearchesDeleteSSAndGroupModalClosed');
            }
          },
          onOk: () => {
            if (path && !hasSiblings) {
              Logger.log(
                'User',
                'SavedSearchesDeleteSSAndGroupModalDeleteClicked',
              );
            }
          },
        }).promise;
        if (path && !hasSiblings) {
          Logger.log('User', 'SavedSearchesDeleteSSAndGroupModalShown');
        }
      } catch (error) {
        // Do nothing, user just closed the dialog
        Logger.log('User', 'ConfirmDeleteSavedSearchNo');
        return null;
      }
      Logger.log('User', 'ConfirmDeleteSavedSearchYes', { savedSearchId: _id });
    }

    try {
      const { data: result } = await sdk.savedSearches.delete(_id);
      if (showToast) {
        const nodes = getState().savedSearches.all;
        const node = nodes.find((ss) => ss._id === _id);
        Toast(l18n.TAGSTREE.textHasBeenRemoved(name), {
          type: 'success',
          btnOkValue: l18n.textBtnUndo,
          onOk: () => dispatch(addSavedSearch({
            name,
            data: node.data,
            shared: !!node.teamId,
            showToast: false,
          })),
        });
      }
      return { result, _id, userId: getState().user._id };
    } catch (error) {
      const errorStatus = utils.getStatusFromResponceError(error);
      if (errorStatus === 403) {
        showErrorDialog(l18n.NO_PERMISSION_TO_ACCESS);
      } else {
        Logger.error(new Error('Can not remove saved searches'), { error }, [
          'RemoveSavedSearchesFailed',
          { errorMessage: (error && error.message) || 'NoMessage' },
        ]);
      }

      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder.addCase(
    remove.pending,
    (state, { meta: { arg: { _id } } }) => {
      state.tree = setField(state.tree, _id, 'isBusy', true);
    },
  ).addCase(
    remove.fulfilled,
    (state, { payload, meta: { arg: { _id } } }) => {
      if (!payload) {
        state.tree = setField(state.tree, _id, 'isBusy', false);
        return;
      }
      const { userId } = payload;
      state.all = state.all.filter((ss) => ss._id !== _id);
      state.tree = makeTree(state.all, userId);
    },
  ).addCase(
    remove.rejected,
    (state, { error, meta: { arg: { _id } } }) => {
      state.error = error;
      state.tree = setField(state.tree, _id, 'isBusy', false);
    },
  );
};
